import { Navigation } from "@mui/icons-material";
import { FunctionComponent, useCallback, useMemo, useState } from "react";
import { renderToString } from "react-dom/server";
import { Marker as MapGLMarker } from "react-map-gl/mapbox";

import { EdrObservationsMapLayerDataLocationsInner, StandardDataUnit } from "@/lib/api";
import { TimeRangeDimensionValue } from "@/lib/dimensions/time-range-dimension";
import { LayerSettingValueSet } from "@/lib/map-layers/layer-settings";
import {
  MapMarker,
  MapMarkerValue,
  MapMarkerWrapper,
} from "@/features/shared/widgets/map/map.styles";

import { EdrParameterSet } from ".";

interface EdrObservationsMarkerProps {
  location: EdrObservationsMapLayerDataLocationsInner;
  onSelect: (location: EdrObservationsMapLayerDataLocationsInner) => void;
  selected: boolean;
  currentTime?: TimeRangeDimensionValue;
  currentSettingValues: LayerSettingValueSet;
}

const EdrObservationsMarker: FunctionComponent<EdrObservationsMarkerProps> = ({
  location,
  onSelect,
  selected,
  currentTime,
  currentSettingValues,
}) => {
  const [hovered, setHovered] = useState(false);
  const handleMarkerClick = useCallback(
    (location: EdrObservationsMapLayerDataLocationsInner) => () => {
      onSelect(location);
    },
    [onSelect],
  );

  const state = useMemo(() => {
    if (selected) return "active";
    if (hovered) return "hover";
    return undefined;
  }, [selected, hovered]);

  const markerValues = useMemo(() => {
    const parameterSet = currentSettingValues.parameter_set.value as EdrParameterSet;
    if (!location.data || !parameterSet || !currentTime) return [];

    return location.data.flatMap((d) => {
      if (!parameterSet.parameters.includes(d.parameter.id)) return [];

      const now = +currentTime.timestamp;
      const point = d.datapoints.find((point) => +new Date(point.timestamp) === now);
      const value = point?.value as unknown;
      const unit = d.parameter.unit;
      if (value === undefined || value === null) return [];
      if (typeof value !== "number" && typeof value !== "string") {
        return [`${value}${unit ? unit.symbol : ""}`];
      } else if (d.parameter.property.id === "wind_from_direction") {
        return [fmtWindCompass(unit, value)];
      } else {
        return [fmtDisplayText(unit, value)];
      }
    });
  }, [location.data, currentSettingValues, currentTime]);

  if (markerValues.length === 0) return null;

  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <MapGLMarker
        key={location.id}
        latitude={location.latitude}
        longitude={location.longitude}
        anchor="bottom"
        style={{
          zIndex: state ? 500 : 100,
        }}
        onClick={handleMarkerClick(location)}
      >
        <MapMarkerWrapper>
          <MapMarker multiple={markerValues.length > 1} state={state}>
            {markerValues.map((v, i, list) => (
              <EdrObservationsMarkerValue
                value={v}
                key={i}
                state={state}
                inverted={list.length > 1 && i === 0}
              />
            ))}
          </MapMarker>
        </MapMarkerWrapper>
      </MapGLMarker>
    </div>
  );
};

interface EdrObservationsMarkerValueProps {
  value: string;
  state?: "active" | "hover";
  inverted: boolean;
}

const EdrObservationsMarkerValue: FunctionComponent<EdrObservationsMarkerValueProps> = ({
  value,
  state,
  inverted,
}) => {
  return (
    <MapMarkerValue
      state={state}
      inverted={inverted}
      dangerouslySetInnerHTML={{
        __html: value,
      }}
    />
  );
};

export default EdrObservationsMarker;

function fmtInteger(value: string | number): string {
  if (typeof value === "number") {
    return Math.round(value).toString();
  } else {
    return value;
  }
}

function fmtValue(value: string | number): string {
  if (typeof value === "number") {
    return Number.isInteger(value) ? value.toString() : value.toFixed(1);
  } else {
    return value;
  }
}

function fmtDisplayText(unit: StandardDataUnit, value: string | number): string {
  switch (unit.type) {
    case "cloud_area_fraction":
      return `${value}/8`;
    case "knots":
    case "foot":
    case "degree":
      return `${fmtInteger(value)}${unit.symbol}`;
    default: {
      return `${fmtValue(value)}${unit.symbol}`;
    }
  }
}
function fmtWindCompass(unit: StandardDataUnit, value: string | number) {
  if (typeof value !== "number") return fmtDisplayText(unit, value);
  const angle = value - 180;
  return `
    <span style="transform: rotate(${angle}deg) scale(0.7)" class="map-marker__value--compass">
      ${renderToString(<Navigation />)}
    </span>
    <span>
      ${fmtDisplayText(unit, value)}
    </span>
  `;
}
